import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, addDoc, getDoc, getDocs, collection, query, where, onSnapshot, orderBy, limit, serverTimestamp, deleteDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { formatDate } from '../utils/dateHelpers';
import { useAuth } from '../components/auth/AuthContext';
import { collectionPath } from '../utils/firestoreCollections';
import { generateLogId, generateContactFocusId } from '../utils/documentIdUtils';
import EditContactModal from '../components/forms/ContactModal';
import { partialUpdateDocument } from '../utils/typesense';
import { monitorQuery } from '../utils/queryMonitoring';
import UnifiedNotes from '../components/common/UnifiedNotes';
import { FEATURE_PERMISSIONS } from '../utils/permissions';
import { usePermissions } from '../hooks/usePermissions';
import PaginationControls from '../components/common/PaginationControls';
import { logEntityChanges } from '../utils/logger';

const LOG_PAGE_SIZE = 25;
const NOTES_LIMIT = 50;
const LOGS_LIMIT = 50;

const ContactDetailPage = ({ user }) => {
    const { contactid, customerId } = useParams(); // contactid is now the ApparelMagic contact ID
    const navigate = useNavigate();
    const location = useLocation();
    const fromContacts = location.state?.fromContacts;
    const { currentUser } = useAuth();
    const { can } = usePermissions(user);
    const [contact, setContact] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [contactFocusOptions, setContactFocusOptions] = useState(['Not Specified', 'Golf', 'Tennis', 'Pickleball', 'Other']);

    // Notes and logs state
    const [notesCount, setNotesCount] = useState(0);
    const [activeTab, setActiveTab] = useState('notes');
    const [logSearch, setLogSearch] = useState('');
    const [logPage, setLogPage] = useState(1);
    const [logs, setLogs] = useState([]);
    const [editModalOpen, setEditModalOpen] = useState(false);

    // Fetch contact from standalone contacts collection, then fetch customer
    useEffect(() => {
        if (!contactid) return;
        
        const fetchContact = async () => {
            try {
                // Fetch contact directly from contacts collection
                const contactSnap = await monitorQuery('fetch-contact-detail', () => 
                    getDoc(doc(db, collectionPath('contacts'), contactid))
                );
                
                if (contactSnap.exists()) {
                    const contactData = { id: contactSnap.id, ...contactSnap.data() };
                    setContact(contactData);
                    
                    // Fetch the parent customer if customerId exists
                    if (contactData.customerId) {
                        const customerSnap = await monitorQuery('fetch-contact-customer', () =>
                            getDoc(doc(db, collectionPath('customers'), contactData.customerId))
                        );
                        if (customerSnap.exists()) {
                            setCustomer({ id: customerSnap.id, ...customerSnap.data() });
                        }
                    }
                } else {
                    console.warn(`Contact not found: ${contactid}`);
                    setContact(null);
                }
            } catch (err) {
                console.error('Error fetching contact:', err);
            }
        };
        
        fetchContact();
    }, [contactid]);

    // Notes are now handled by UnifiedNotes component
    // This effect is kept only for notes count in tab badge
    useEffect(() => {
        if (!contactid) return;
        const notesQ = query(
            collection(db, collectionPath('notes')),
            where('associations.contactId', '==', contactid),
            orderBy('createdAt', 'desc'),
            limit(NOTES_LIMIT)
        );
        const unsubscribe = onSnapshot(notesQ, (notesSnap) => {
            setNotesCount(notesSnap.size);
        });
        return () => unsubscribe();
    }, [contactid]);

    // Listen for logs
    useEffect(() => {
        if (!contactid) return;
        const logsQ = query(
            collection(db, collectionPath('logsContact')),
            where('contactId', '==', contactid),
            orderBy('timestamp', 'desc'),
            limit(LOGS_LIMIT)
        );
        const unsub = onSnapshot(logsQ, snap => {
            setLogs(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsub();
    }, [contactid]);

    // Fetch contact focus options from Firestore
    useEffect(() => {
        const fetchContactFocusOptions = async () => {
            try {
                const snap = await monitorQuery('fetch-contact-focus-options', () =>
                    getDocs(collection(db, collectionPath('contactFocus')))
                );
                const customOptions = snap.docs.map(doc => doc.data().name).filter(Boolean);
                const allOptions = [...new Set([...contactFocusOptions, ...customOptions])];
                setContactFocusOptions(allOptions);
            } catch (err) {
                console.warn('Could not fetch contact focus options:', err);
            }
        };
        fetchContactFocusOptions();
    }, []);

    // Filtered logs for activity tab
    const filteredLogs = useMemo(() => {
        const q = (logSearch || '').toLowerCase().trim();
        if (!q) return logs;
        return logs.filter(log =>
            (log.action || '').toLowerCase().includes(q) ||
            (log.details || '').toLowerCase().includes(q) ||
            (log.userEmail || '').toLowerCase().includes(q) ||
            (log.timestamp && log.timestamp.toDate && log.timestamp.toDate().toLocaleString().toLowerCase().includes(q))
        );
    }, [logs, logSearch]);

    const totalLogPages = Math.ceil(filteredLogs.length / LOG_PAGE_SIZE);
    const paginatedLogs = filteredLogs.slice((logPage - 1) * LOG_PAGE_SIZE, logPage * LOG_PAGE_SIZE);

    useEffect(() => { setLogPage(1); }, [logSearch, logs.length]);

    // Handler for saving contact edits
    const handleSaveContact = async (updatedContact) => {
        try {
            // Normalize contactFocus: ContactModal sends a clean array, but guard against
            // any legacy string or stringified-array values that may have crept in.
            let normalizedFocus = updatedContact.contactFocus;
            if (!Array.isArray(normalizedFocus)) {
                if (typeof normalizedFocus === 'string' && normalizedFocus && normalizedFocus !== '[]') {
                    normalizedFocus = normalizedFocus.split(',').map(s => s.trim()).filter(Boolean);
                } else {
                    normalizedFocus = [];
                }
            }
            const safeContact = { ...updatedContact, contactFocus: normalizedFocus };

            // Write to Firestore
            const { id: _id, ...contactFields } = safeContact;
            await updateDoc(doc(db, collectionPath('contacts'), contactid), {
                ...contactFields,
                updatedAt: serverTimestamp(),
            });

            // Keep Typesense index in sync so the Contacts list reflects changes immediately
            try {
                const existing = contact || {};
                const createdAtSecs = existing.createdAt?.seconds != null
                    ? Math.floor(existing.createdAt.seconds)
                    : existing.createdAt?.toMillis != null
                        ? Math.floor(existing.createdAt.toMillis() / 1000)
                        : Math.floor(Date.now() / 1000);
                await partialUpdateDocument('contacts', {
                    id: contactid,
                    firstName: safeContact.firstName || undefined,
                    lastName: safeContact.lastName || undefined,
                    email: safeContact.email || undefined,
                    phone: safeContact.phone || undefined,
                    title: safeContact.title || undefined,
                    contactFocus: normalizedFocus.length > 0 ? normalizedFocus : undefined,
                    isPrimary: safeContact.isPrimary === true,
                    customerId: existing.customerId || undefined,
                    customerName: existing.customerName || undefined,
                    customerAccountNumber: existing.customerAccountNumber || undefined,
                    salesRepNames: Array.isArray(existing.salesRepNames) && existing.salesRepNames.length > 0
                        ? existing.salesRepNames : undefined,
                    createdAt: createdAtSecs,
                });
            } catch (tsErr) {
                console.warn('[ContactDetailPage] Typesense update failed (non-fatal):', tsErr);
            }

            // Log the change
            await logEntityChanges({
                oldObj: contact,
                newObj: safeContact,
                fields: ['firstName', 'lastName', 'email', 'phone', 'title', 'contactFocus', 'isPrimary'],
                entityType: 'Contact',
                entityId: contactid,
                user: currentUser,
                action: contact ? 'Updated' : 'Created',
            });
            setContact({ ...safeContact, id: contactid });
            setEditModalOpen(false);
        } catch (err) {
            console.error('Failed to save contact edit:', err);
            setContact({ ...updatedContact, id: contactid });
            setEditModalOpen(false);
        }
    };

    if (!contact) return <div className="p-6">Contact not found.</div>;

    return (
        <main className="p-6">
            <div className="flex items-center mb-4">
                <button
                    onClick={() => fromContacts ? navigate('/contacts') : navigate(`/customer/${customerId}/`)}
                    className="text-indigo-600 hover:text-indigo-500 mr-4"
                >
                    {fromContacts ? '← Back to Contacts' : '← Back to Customer'}
                </button>
                <h2 className="text-2xl font-bold">Contact Details</h2>
            </div>
            <div className="bg-white rounded shadow p-4 mb-6 relative">
                {can(FEATURE_PERMISSIONS.CONTACT_EDIT) && (
                    <button
                        className="absolute top-4 right-4 px-4 py-2 bg-indigo-600 text-white rounded shadow hover:bg-indigo-700"
                        onClick={() => setEditModalOpen(true)}
                    >
                        Edit
                    </button>
                )}
                <div className="mb-2"><span className="font-semibold">Name:</span> {[contact.firstName || contact.contactFirstName, contact.lastName || contact.contactLastName].filter(Boolean).join(' ')}</div>
                <div className="mb-2"><span className="font-semibold">Email:</span> {contact.email || contact.contactEmail || '-'}</div>
                <div className="mb-2"><span className="font-semibold">Phone:</span> {contact.phone || contact.contactPhone || '-'}</div>
                <div className="mb-2"><span className="font-semibold">Title:</span> {contact.title || contact.contactTitle || contact.contactPosition || '-'}</div>
                <div className="mb-2"><span className="font-semibold">Focus:</span> {Array.isArray(contact.contactFocus) ? contact.contactFocus.join(', ') : (contact.contactFocus || '-')}</div>
                <div className="mb-2"><span className="font-semibold">Business Type:</span> {customer?.businessType || '-'}</div>
                <div className="mb-2"><span className="font-semibold">Primary Contact:</span> {contact.isPrimary ? 'Yes' : 'No'}</div>
                <div className="mb-2"><span className="font-semibold">Customer:</span> {customer?.customerName || customer?.name || '-'}</div>
            </div>
            {/* Tabs for Notes and Activity Log */}
            <div className="bg-white rounded shadow p-4">
                <div className="border-b border-gray-200 mb-4">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('notes')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'notes'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Notes
                        </button>
                        <button
                            onClick={() => setActiveTab('activity')}
                            className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                activeTab === 'activity'
                                    ? 'border-indigo-500 text-indigo-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            Activity Log
                        </button>
                    </nav>
                </div>
                {activeTab === 'notes' ? (
                    <div className="p-4">
                        <UnifiedNotes 
                            entityType="contact" 
                            entityId={contactid}
                            customerId={customerId}
                            maxNotes={100}
                            onNotesLoaded={setNotesCount}
                        />
                    </div>
                ) : (
                    activeTab === 'activity' && (
                        <div className="overflow-x-auto">
                            <div className="flex items-center mb-3">
                                <span className="mr-2 font-medium text-gray-700">Search:</span>
                                <input
                                    type="text"
                                    className={`w-full border rounded px-3 py-2 transition-colors duration-150 ${
                                        logSearch ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                                    }`}
                                    placeholder="Search activity log..."
                                    value={logSearch}
                                    onChange={e => setLogSearch(e.target.value)}
                                />
                            </div>
                            <table className="w-full whitespace-no-wrap">
                                <thead>
                                    <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                                        <th className="px-4 py-3">Date</th>
                                        <th className="px-4 py-3">Time</th>
                                        <th className="px-4 py-3">User</th>
                                        <th className="px-4 py-3">Action</th>
                                        <th className="px-4 py-3">Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedLogs.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="text-center py-4 text-gray-500">No activity found.</td>
                                        </tr>
                                    ) : (
                                        paginatedLogs.map(log => {
                                            const dateObj = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
                                            return (
                                                <tr key={log.id} className="text-gray-700 hover:bg-gray-50">
                                                    <td className="px-4 py-3">{formatDate(dateObj, currentUser?.dateFormat)}</td>
                                                    <td className="px-4 py-3">{dateObj.toLocaleTimeString()}</td>
                                                    <td className="px-4 py-3">{log.userEmail || log.userId || '-'}</td>
                                                    <td className="px-4 py-3">{log.action || '-'}</td>
                                                    <td className="px-4 py-3">{log.details || '-'}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                            <PaginationControls
                                currentPage={logPage}
                                totalPages={totalLogPages}
                                onPageChange={setLogPage}
                            />
                        </div>
                    )
                )}
            </div>
            <EditContactModal
                open={editModalOpen}
                contact={{
                    ...contact,
                    firstName: contact.firstName ?? contact.contactFirstName ?? '',
                    lastName: contact.lastName ?? contact.contactLastName ?? '',
                    email: contact.email ?? contact.contactEmail ?? '',
                    phone: contact.phone ?? contact.contactPhone ?? '',
                    title: contact.title ?? contact.contactTitle ?? contact.contactPosition ?? '',
                    businessType: contact.businessType ?? customer?.businessType ?? '',
                    contactFocus: (
                        Array.isArray(contact.contactFocus) ? contact.contactFocus :
                        typeof contact.contactFocus === 'string' && contact.contactFocus ? contact.contactFocus.split(',').map(s => s.trim()).filter(Boolean) :
                        Array.isArray(contact.businessFocus) ? contact.businessFocus :
                        typeof contact.businessFocus === 'string' && contact.businessFocus ? contact.businessFocus.split(',').map(s => s.trim()).filter(Boolean) :
                        Array.isArray(contact.contactBusinessFocus) ? contact.contactBusinessFocus :
                        typeof contact.contactBusinessFocus === 'string' && contact.contactBusinessFocus ? contact.contactBusinessFocus.split(',').map(s => s.trim()).filter(Boolean) :
                        Array.isArray(contact.focus) ? contact.focus :
                        typeof contact.focus === 'string' && contact.focus ? contact.focus.split(',').map(s => s.trim()).filter(Boolean) :
                        []
                    ),
                    isPrimary: contact.isPrimary ?? contact.contactIsPrimary ?? false,
                    customerId: customerId,
                }}
                onClose={() => setEditModalOpen(false)}
                onSave={handleSaveContact}
                businessTypeOptions={[]}
                contactFocusOptions={contactFocusOptions}
                persistNewBusinessType={() => {}}
                persistNewContactFocus={async (name, setValue) => {
                    if (!name.trim()) return;
                    const trimmed = name.trim();
                    if (!contactFocusOptions.includes(trimmed)) {
                        try {
                            const focusId = generateContactFocusId(trimmed);
                            await setDoc(doc(db, collectionPath('contactFocus'), focusId), {
                                name: trimmed,
                                createdAt: serverTimestamp(),
                                createdBy: currentUser?.uid || null
                            });
                            setContactFocusOptions(prev => [...prev, trimmed]);
                        } catch (err) {
                            console.error('Failed to persist contact focus', err);
                            return;
                        }
                    }
                    if (setValue) setValue(trimmed);
                }}
                customers={[customer]}
            />
        </main>
    );
};

export default ContactDetailPage;



